import { useMemo } from 'react'
import { Chip, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Checkbox from 'packages/ui/Checkbox'
import EmptyScreen from 'packages/ui/EmptyScreen'
import TableView, { useTable, IColumn } from 'packages/ui/Table'
import ScrollTable from 'components/global/CUK/ScrollTable'

import { useSort, useSelection } from './store'
import { AlarmChip } from './Alarm'
import { IAlarm } from './use-sips'

const HeaderCheckbox = (ctx) => {
  const [selection, setSelection] = useSelection()
  return (
    <Checkbox
      checked={ctx.items.length && ctx.items.length === selection.length}
      indeterminate={Boolean(selection.length) && ctx.items.length !== selection.length}
      onChange={() => {
        if (selection.length) {
          setSelection([])
        } else {
          setSelection(ctx.items)
        }
      }}
    />
  )
}

const BodyCheckbox = ({ item }) => {
  const [selection, setSelection] = useSelection()
  const exist = useMemo(() => {
    return selection.some(({ room }) => room === item.room)
  }, [item, selection])
  return (
    <Checkbox
      checked={exist}
      onChange={(e) => {
        e.stopPropagation()
        if (exist) {
          setSelection(prev => prev.filter(({ room }) => room !== item.room))
        } else {
          setSelection(prev => [...prev, item]);
        }
      }}
    />
  )
}

const Status = ({ label, color, bgcolor }) => {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        borderRadius: '50px',
        height: 24,
        backgroundColor: bgcolor,
        color,
        fontWeight: 500,
        fontSize: 10,
      }}
    />
  )
}


const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'check',
    header: {
      label: HeaderCheckbox,
    },
    style: {
      width: 24,
      paddingLeft: 8,
    },
    value: item => <BodyCheckbox item={item} />,
  },
  {
    name: 'status',
    header: {
      label: ctx => ctx.t('voip.sip.status')
    },
    value: ({ status }, ctx) => {
      return (
        <Status
          label={ctx?.t(`voip.sip.status_${status}`)}
          color={status === 'up' ? '#587F31' : '#DC2626'}
          bgcolor={status === 'up' ? '#E6F0D7' : '#FEE2E2'}
        />
      )
    },
    sortable: true
  }, {
    name: 'user',
    header: {
      label: ctx => ctx.t('voip.sip.user')
    },
    value: ({ user }) => (user as string) || '-',
    sortable: true
  }, {
    name: 'room',
    header: {
      label: ctx => ctx.t('voip.sip.room')
    },
    value: ({ room }) => (room as string) || '-',
    sortable: true
  }, {
    name: 'phone_long',
    header: {
      label: ctx => ctx.t('voip.sip.phone_long')
    },
    value: ({ phone_long }) => phone_long as string,
    sortable: true
  }, {
    name: 'phone_short',
    header: {
      label: ctx => ctx.t('voip.sip.phone_short')
    },
    value: ({ phone_short }) => phone_short as string,
    sortable: true
  }, {
    name: 'calling_profile',
    header: {
      label: ctx => ctx.t('voip.sip.calling_profile_label')
    },
    value: ({ calling_profile }, ctx) => ctx?.t(`voip.sip.calling_profile.${calling_profile as string}`),
    sortable: true
  }, {
    name: 'last_connection',
    header: {
      label: ctx => ctx.t('voip.sip.last_connection')
    },
    value: ({ status, last_connection }, ctx) => {
      if (status === 'down') {
        return last_connection
          ? ctx?.t('voip.datetime', {
            date: new Date(last_connection as string).toLocaleDateString(),
            time: new Date(last_connection as string).toLocaleTimeString()
          })
          : '-'
      }
      return ctx?.t('voip.sip.online')
    },
    sortable: true
  }, {
    name: 'alarm',
    header: {
      label: ctx => ctx.t('voip.sip.alarm.header')
    },
    value: ({ alarm, room }) => {
      return <AlarmChip alarm={alarm as unknown as IAlarm} room_number={room as string} />
    }
  }
]

const Table = ({ loading, sips, type }) => {
  const { t } = useTranslation()
  const [sort, setSort] = useSort()
  const [selection] = useSelection()
  const apiTable = useTable({
    rowKey: 'phone_number',
    items: sips,
    loading,
    columns: COLUMNS_CONFIG.filter(({ name }) => {
      if (['last_connection', 'calling_profile'].includes(name)) {
        return selection.length === 0
      }
      if (name === 'user') {
        return type === 'administrative'
      } else if (['room', 'alarm', 'check'].includes(name)) {
        return type === 'room'
      }
      return true
    })
  })

  if (!loading && (!sips || sips.length === 0)) {
    return (
      <Stack
        sx={{
          height: 'calc(100cqh - 132px)',
          border: '1px dashed #D7DAEA',
          borderRadius: '24px',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <EmptyScreen icon={'phone-volume'} variant="compact">
          {t('voip.phone_numbers.no_items')}
        </EmptyScreen>
      </Stack>
    )
  }

  return (
    <ScrollTable
      shouldFetchNextPage={false}
      collapse={selection.length > 0}
    >
      <TableView
        apiTable={apiTable}
        sort={{
          ...sort,
          onChange: (name) => {
            setSort(name)
          }
        }}
        noItemsMessage={t('voip.phone_numbers.no_items')}
      />
    </ScrollTable>
  )
}

export default Table